// Service Worker Registration and Management
// Progressive Web App functionality for Custom Design Manager

interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOfflineReady?: () => void;
}

interface PerformanceData {
  cacheHits: number;
  cacheMisses: number;
  networkRequests: number;
  offlineRequests: number;
}

class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private updateAvailable = false;
  private performanceData: PerformanceData | null = null;

  /**
   * Register the service worker
   */
  async register(config: ServiceWorkerConfig = {}): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers are not supported in this browser');
      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log('Service worker registration skipped in development');
      return;
    }

    try {
      console.log('Registering service worker...');
      
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      this.registration = registration;

      // Handle different registration states
      if (registration.installing) {
        console.log('Service worker installing...');
        this.trackInstalling(registration.installing, config);
      } else if (registration.waiting) {
        console.log('Service worker waiting...');
        this.handleWaiting(registration, config);
      } else if (registration.active) {
        console.log('Service worker active');
        config.onSuccess?.(registration);
      }

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        console.log('Service worker update found');
        const newWorker = registration.installing;
        if (newWorker) {
          this.trackInstalling(newWorker, config);
        }
      });

      // Handle controller changes
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('Service worker controller changed');
        window.location.reload();
      });

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', this.handleMessage);

      console.log('Service worker registered successfully');
      
      // Initial performance data fetch
      setTimeout(() => this.getPerformanceData(), 1000);

    } catch (error) {
      console.error('Service worker registration failed:', error);
    }
  }

  /**
   * Track service worker installation
   */
  private trackInstalling(worker: ServiceWorker, config: ServiceWorkerConfig): void {
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed') {
        if (navigator.serviceWorker.controller) {
          // New update available
          console.log('New content available; please refresh.');
          this.updateAvailable = true;
          config.onUpdate?.(this.registration!);
        } else {
          // Content cached for offline use
          console.log('Content cached for offline use.');
          config.onSuccess?.(this.registration!);
          config.onOfflineReady?.();
        }
      }
    });
  }

  /**
   * Handle waiting service worker
   */
  private handleWaiting(registration: ServiceWorkerRegistration, config: ServiceWorkerConfig): void {
    if (registration.waiting) {
      this.updateAvailable = true;
      config.onUpdate?.(registration);
    }
  }

  /**
   * Handle messages from service worker
   */
  private handleMessage = (event: MessageEvent): void => {
    const { data } = event;
    
    if (data && typeof data === 'object') {
      // Update performance data
      if ('cacheHits' in data) {
        this.performanceData = data as PerformanceData;
      }
      
      // Handle cache clear confirmation
      if ('success' in data) {
        console.log('Cache operation result:', data);
      }
    }
  };

  /**
   * Update the service worker
   */
  async updateServiceWorker(): Promise<void> {
    if (!this.registration || !this.registration.waiting) {
      console.warn('No service worker waiting to update');
      return;
    }

    try {
      // Tell the waiting service worker to skip waiting
      this.postMessage({ type: 'SKIP_WAITING' });
      
      console.log('Service worker update initiated');
    } catch (error) {
      console.error('Failed to update service worker:', error);
    }
  }

  /**
   * Get performance data from service worker
   */
  async getPerformanceData(): Promise<PerformanceData | null> {
    return new Promise((resolve) => {
      if (!navigator.serviceWorker.controller) {
        resolve(null);
        return;
      }

      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        this.performanceData = event.data;
        resolve(event.data);
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_PERFORMANCE_DATA' },
        [channel.port2]
      );

      // Timeout after 5 seconds
      setTimeout(() => resolve(this.performanceData), 5000);
    });
  }

  /**
   * Clear all caches
   */
  async clearCaches(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!navigator.serviceWorker.controller) {
        resolve(false);
        return;
      }

      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        resolve(event.data.success);
      };

      navigator.serviceWorker.controller.postMessage(
        { type: 'CLEAR_CACHE' },
        [channel.port2]
      );

      // Timeout after 10 seconds
      setTimeout(() => resolve(false), 10000);
    });
  }

  /**
   * Post message to service worker
   */
  private postMessage(message: any): void {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage(message);
    }
  }

  /**
   * Check if update is available
   */
  isUpdateAvailable(): boolean {
    return this.updateAvailable;
  }

  /**
   * Get current performance data
   */
  getCurrentPerformanceData(): PerformanceData | null {
    return this.performanceData;
  }

  /**
   * Unregister service worker (for development/testing)
   */
  async unregister(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      
      for (const registration of registrations) {
        await registration.unregister();
      }
      
      console.log('All service workers unregistered');
    } catch (error) {
      console.error('Failed to unregister service workers:', error);
    }
  }

  /**
   * Check if app is running in standalone mode (PWA)
   */
  static isPWA(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true ||
           document.referrer.includes('android-app://');
  }

  /**
   * Check if browser supports PWA installation
   */
  static supportsPWAInstall(): boolean {
    return 'serviceWorker' in navigator && 
           'PushManager' in window &&
           'Notification' in window;
  }

  /**
   * Get cache storage estimate
   */
  static async getStorageEstimate(): Promise<StorageEstimate | null> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        return await navigator.storage.estimate();
      } catch (error) {
        console.error('Failed to get storage estimate:', error);
      }
    }
    return null;
  }

  /**
   * Request persistent storage
   */
  static async requestPersistentStorage(): Promise<boolean> {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      try {
        return await navigator.storage.persist();
      } catch (error) {
        console.error('Failed to request persistent storage:', error);
      }
    }
    return false;
  }
}

// Singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

// Convenience functions
export const registerSW = (config?: ServiceWorkerConfig) => 
  serviceWorkerManager.register(config);

export const updateSW = () => 
  serviceWorkerManager.updateServiceWorker();

export const getSWPerformanceData = () => 
  serviceWorkerManager.getPerformanceData();

export const clearSWCaches = () => 
  serviceWorkerManager.clearCaches();

export const isPWA = ServiceWorkerManager.isPWA;

export const supportsPWAInstall = ServiceWorkerManager.supportsPWAInstall;

// Types
export type { ServiceWorkerConfig, PerformanceData };